import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
} from "reactflow";
import "reactflow/dist/style.css";

interface CRMFormField {
  id: number | string;
  type: string;
  label: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface FieldMapping {
  id?: number;
  form_id: string;
  crm_form_id: number;
  source_field: string;
  source_field_label?: string;
  target_field_id: string;
  target_field_label: string;
  mapping_type: "automatic" | "manual" | "custom";
  is_active: boolean;
  mapping_order?: number;
  transformation_rule?: string;
  created_at?: string;
}

interface FieldMappingFlowProps {
  facebookFields: string[];
  crmFormFields: CRMFormField[];
  existingMappings: FieldMapping[];
  onMappingsChange: (mappings: FieldMapping[]) => void;
  onClose: () => void;
  selectedFormForMapping: any;
  selectedCrmForm: any;
}

const FieldMappingFlow: React.FC<FieldMappingFlowProps> = ({
  facebookFields,
  crmFormFields,
  existingMappings,
  onMappingsChange,
  onClose,
  selectedFormForMapping,
  selectedCrmForm,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Function to replace country field with location fields
  const replaceCountryWithLocationFields = useCallback(
    (fields: CRMFormField[]): CRMFormField[] => {
      const expandedFields: CRMFormField[] = [];

      fields.forEach((field) => {
        if (field.type === "country") {
          // Replace the country field with four location fields
          // Country field
          expandedFields.push({
            id: `${field.id}-country`,
            type: "select",
            label: "Country",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          // State field
          expandedFields.push({
            id: `${field.id}-state`,
            type: "select",
            label: "State",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          // District field
          expandedFields.push({
            id: `${field.id}-district`,
            type: "select",
            label: "District",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          // City field
          expandedFields.push({
            id: `${field.id}-city`,
            type: "select",
            label: "City",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });
        } else {
          // Add non-country fields as-is
          expandedFields.push(field);
        }
      });

      return expandedFields;
    },
    []
  );

  // Initialize nodes from fields
  useEffect(() => {
    const facebookNodes: Node[] = facebookFields.map((field, index) => ({
      id: `facebook-${field}`,
      type: "input",
      position: { x: 100, y: 100 + index * 100 },
      data: {
        label: field,
        type: "facebook",
        fieldName: field,
      },
      style: {
        background: "#e3f2fd",
        border: "2px solid #2196f3",
        borderRadius: "8px",
        padding: "10px",
        width: 200,
      },
    }));

    // Replace country fields with location fields
    const expandedCrmFields = replaceCountryWithLocationFields(crmFormFields);

    const crmNodes: Node[] = expandedCrmFields.map((field, index) => ({
      id: `crm-${field.id}`,
      type: "output",
      position: { x: 500, y: 100 + index * 100 },
      data: {
        label: `${field.label} ${field.required ? "*" : ""}`,
        type: "crm",
        fieldId: field.id,
        fieldType: field.type,
        required: field.required,
      },
      style: {
        background: field.required ? "#ffebee" : "#e8f5e8",
        border: `2px solid ${field.required ? "#f44336" : "#4caf50"}`,
        borderRadius: "8px",
        padding: "10px",
        width: 200,
      },
    }));

    setNodes([...facebookNodes, ...crmNodes]);
  }, [facebookFields, crmFormFields, replaceCountryWithLocationFields]);

  // Initialize edges from existing mappings
  useEffect(() => {
    const initialEdges: Edge[] = existingMappings.map((mapping) => ({
      id: `edge-${mapping.source_field}-${mapping.target_field_id}`,
      source: `facebook-${mapping.source_field}`,
      target: `crm-${mapping.target_field_id}`,
      style: { stroke: "#ff6b6b", strokeWidth: 2 },
      animated: true,
    }));

    setEdges(initialEdges);
  }, [existingMappings]);

  const onConnect = useCallback(
    (params: Connection) => {
      const sourceField = params.source?.replace("facebook-", "");
      const targetFieldId = params.target?.replace("crm-", "");

      // Find the field in expanded fields
      const expandedCrmFields = replaceCountryWithLocationFields(crmFormFields);
      const targetField = expandedCrmFields.find(
        (f) => f.id.toString() === targetFieldId
      );

      if (
        !sourceField ||
        !targetField ||
        !selectedFormForMapping ||
        !selectedCrmForm
      ) {
        return;
      }

      // Check if this source field already has a mapping
      const existingMappingIndex = existingMappings.findIndex(
        (m) => m.source_field === sourceField
      );

      let updatedMappings: FieldMapping[];

      if (existingMappingIndex !== -1) {
        // UPDATE CASE: Source field already mapped, update the target
        updatedMappings = existingMappings.map((mapping, index) =>
          index === existingMappingIndex
            ? {
                ...mapping,
                target_field_id: targetField.id.toString(),
                target_field_label: targetField.label,
                mapping_type: "manual",
              }
            : mapping
        );
      } else {
        // CREATE CASE: New source field mapping
        const newMapping: FieldMapping = {
          source_field: sourceField,
          source_field_label: sourceField,
          target_field_id: targetField.id.toString(),
          target_field_label: targetField.label,
          crm_form_id: selectedCrmForm.key,
          form_id: selectedFormForMapping.form_id,
          mapping_type: "manual",
          is_active: true,
        };
        updatedMappings = [...existingMappings, newMapping];
      }

      // Update the edges to reflect the new connection
      const newEdge = {
        ...params,
        id: `edge-${sourceField}-${targetFieldId}`,
        style: { stroke: "#ff6b6b", strokeWidth: 2 },
        animated: true,
      };

      // Remove any existing edge from this source and add the new one
      setEdges((eds) => {
        const filteredEdges = eds.filter(
          (edge) => edge.source !== params.source
        );
        return addEdge(newEdge, filteredEdges);
      });

      // Update the mappings state
      onMappingsChange(updatedMappings);
    },
    [
      crmFormFields,
      existingMappings,
      onMappingsChange,
      selectedFormForMapping,
      selectedCrmForm,
      replaceCountryWithLocationFields,
    ]
  );

  // Handle edge deletion (when user removes a connection)
  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      deletedEdges.forEach((edge) => {
        const sourceField = edge.source?.replace("facebook-", "");
        if (sourceField) {
          // Remove the mapping for this source field
          const updatedMappings = existingMappings.filter(
            (m) => m.source_field !== sourceField
          );
          onMappingsChange(updatedMappings);
        }
      });
    },
    [existingMappings, onMappingsChange]
  );

  const nodeColor = (node: Node) => {
    switch (node.data.type) {
      case "facebook":
        return "#e3f2fd";
      case "crm":
        return node.data.required ? "#ffebee" : "#e8f5e8";
      default:
        return "#eee";
    }
  };

  return (
    <div style={{ width: "88%", height: "600px" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgesDelete={onEdgesDelete}
        fitView
      >
        <Background />
        <Controls />
        <MiniMap nodeColor={nodeColor} nodeStrokeWidth={3} zoomable pannable />
      </ReactFlow>

      <div className="mt-3">
        <div className="row">
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 20,
                  height: 20,
                  background: "#e3f2fd",
                  border: "2px solid #2196f3",
                  marginRight: 8,
                }}
              ></div>
              <span>Facebook Fields</span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 20,
                  height: 20,
                  background: "#e8f5e8",
                  border: "2px solid #4caf50",
                  marginRight: 8,
                }}
              ></div>
              <span>CRM Fields (Green = Optional, Red = Required)</span>
            </div>
          </div>
        </div>

        <div className="mt-2 text-muted">
          <small>
            💡 Drag from Facebook fields to CRM fields to create mappings. Drag
            an existing connection to a new CRM field to update it. Click on a
            connection and press Delete to remove it.
            <br />
            🌍 <strong>Location Fields:</strong> Country type fields are
            automatically expanded into Country, State, District, and City
            fields.
          </small>
        </div>
      </div>
    </div>
  );
};

export default FieldMappingFlow;
